<template>
    <div id="ReportingTable">
        <ErrorList v-if="error" :error="error"/>
        <div v-if="!gatherPayroll" class="transaction-summary">
            <h2 class="transaction-summary__header">Transaction Summary</h2>
            <button id="submit" class="transaction-summary__print-button button-sm purple" @click="print">Print</button>
        </div>

        <div v-for="[accountName, accountDetail] in accountDetails" :key="accountName" class="account-report">
            <div v-if="isMultipleAccounts" class="account-report__account-name">{{ accountName }}</div>
            <div v-for="(policy, index) in accountDetail.policies" :key="index" class="policy-report" :class="{ 'policy-report--multiple-accounts': isMultipleAccounts }">

                <div class="policy-info">{{ policy.name }}</div>

                <div v-if="policy.firstReport === true" style="font-size: 1.5em; margin-bottom: 0.5em; padding-left: 1em;">
                    <font-awesome-icon :icon="['fas', 'info-circle']" style="color: #792061;"/> The first payroll report for this new policy will include a one-time <em>expense constant</em> charge of {{ policy.expenseConstant | currency}}.
                </div>

                <template v-if="(gatherPayroll && ! policy.hasReportedPayroll) || policy.hasPayrollReport === true">
                    <div v-for="(jurisdiction, j) in policy.jurisdictions" :key="j" class="JurisdictionReport">
                        <div v-for="(location, k) in jurisdiction.locations" :key="k" class="LocationReport">
                            <table class="LocationReportingTable">
                                <caption>{{ stateAbbreviationToFullName[location.state] }} Payroll
                                    <span v-if="isPolicyTransitioningThisMonth(policy, reporting)"
                                          class="ReportingAdvice">&#128681; Only report payroll between {{ startPeriod(policy) }} and {{ endPeriod(policy) }}</span>
                                </caption>
                                <thead>
                                <tr>
                                    <th scope="col" style="width: 10%">Class</th>
                                    <th scope="col" style="width: 60%">Description</th>
                                    <th scope="col" style="width: 15%">Payroll</th>
                                    <th scope="col" style="width: 15%">Premium Due</th>
                                </tr>
                                </thead>

                                <tbody>
                                <tr v-for="(clazz, index) in location.classCodes" :key="index">
                                    <td data-label="Class">{{ clazz.classCode }}</td>
                                    <td data-label="Description">{{ clazz.description }}</td>
                                    <td v-if="gatherPayroll" data-label="Payroll" class="alignCorrectly">
                                        <input type="text" v-model="clazz.payroll" :class="[isPayrollFieldInvalid(clazz.payroll) ? 'invalid' : '']" @change="toggleError" @keydown.space.prevent maxlength="12"/>
                                    </td>
                                    <td v-else data-label="Payroll" class="alignCorrectly">{{ clazz.payroll | toCurrency }}</td>
                                    <td data-label="Premium Due" class="alignCorrectly">{{ premiumDue(clazz) | currency }}</td>
                                </tr>
                                </tbody>

                                <tfoot>
                                <tr>
                                    <td>&nbsp;</td>
                                    <td colspan="2" class="alignCorrectly lighter">Premium total:</td>
                                    <td class="alignCorrectly">{{ premiumTotal(location) | currency }}</td>
                                </tr>
                                <tr>
                                    <td>&nbsp;</td>
                                    <td colspan="2" class="alignCorrectly lighter">Taxes/assessments total:</td>
                                    <td class="alignCorrectly">{{ taxTotal(location) | currency }}</td>
                                </tr>
                                <tr class="grandtotal">
                                    <td>&nbsp;</td>
                                    <td colspan="2" class="alignCorrectly lighter">{{ stateAbbreviationToFullName[location.state] }} total:</td>
                                    <td class="alignCorrectly">{{ (taxTotal(location) + premiumTotal(location)) | currency }}</td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </template>
                <div v-else class="PolicyAlreadyReported">
                    Payroll has already been <router-link :to="{ name: 'PaymentHistory' }">reported</router-link> for this policy.
                </div>
            </div>
        </div>
        <div id="ReportingBottom" v-if="gatherPayroll">
            <div id="ReportingAssistance"><p>For assistance with reporting payroll,<br>
                view the <router-link to="/instructions">Reporting Instructions</router-link>.</p></div>
            <div id="ReportingButtons">
                <button id="clear" class="button-sm gray" @click="onClear">Clear</button>
                <button id="submit" class="button-sm purple" @click="onSubmit">Submit</button>
            </div>
        </div>
    </div>
</template>

<script>
import {store} from "@/utils/storage";
import {mapState} from "vuex";
import mixin from "./mixins/confAndReportingMixin";
import {startPeriod, endPeriod} from "@/utils/helpers";
import ErrorList from "./shared/ErrorList";

export default {
    mixins: [mixin],
    name: "ReportingTable",
    components: {ErrorList},
    computed: {
        ...mapState(["policies", "contact", "stateAbbreviationToFullName", "reporting"]),
        accountDetails: function() {
            const accountDetails = new Map();
            for (const policy of this.policies) {
                let accountName = policy.accountName
                let accountDetail = accountDetails.get(accountName)
                if (! accountDetail) {
                    accountDetails.set(accountName, { policies: [ policy ] })
                } else {
                    accountDetail.policies.push(policy)
                }
            }
            return accountDetails
        },
        isMultipleAccounts: function () {
            return this.accountDetails.size > 1
        },
        hasError: function() {
            return this.error != null
        }
    },
    data() {
        return {
            error: null
        }
    },
    methods: {
        startPeriod(policy) {
            return startPeriod(policy, this.reporting.date)
        },
        endPeriod(policy) {
            return endPeriod(policy, this.reporting.date)
        },
        createErrorWithMessage(message) {
            return {message: message};
        },
        isPayrollFieldInvalid(payroll) {
            return this.hasError && this.isPayrollValid(payroll)
        },
        isPayrollValid(payroll) {
            return (payroll == null || !/^\d+(\.\d{2})?$/.test(payroll))
        },
        onClear() {
            this.policies.filter(pol => ! pol.hasReportedPayroll).forEach(pol => {
                pol.jurisdictions.forEach(jurisdiction => {
                    jurisdiction.locations.forEach(loc => {
                        loc.classCodes.forEach(clazz => {
                            clazz.payroll = ""
                        })
                    })
                })
            })
        },
        print() {
            window.print()
        },
        toggleError() {
            this.error = null
        },
        countPolicyReportsNeeded(policies) {
            return policies.filter(policy => ! policy.hasReportedPayroll).length
        },
        validate(policy) {
            let valid = true
            policy.jurisdictions.forEach(jurisdiction => {
                jurisdiction.locations.forEach(location => {
                    location.classCodes.forEach(clazz => {
                        if (!clazz.payroll) {
                            this.error = this.createErrorWithMessage("Each payroll line must have a value.  If there is no payroll to report, please enter a zero.")
                            valid = false
                        } else if ((!/(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/.test(clazz.payroll) || clazz.payroll === ".") && clazz.payroll) {
                            this.error = this.createErrorWithMessage("Payroll must be in number format with only 2 places after decimal.")
                            valid = false;
                        }
                    })
                })
            })
            if (valid && this.error == null) {
                this.error = null
            }
            return valid
        },
        onSubmit(e) {
            e.preventDefault()
            e.target.blur()

            let payrollReports = []
            this.policies.forEach(pol => {
                pol.hasPayrollReport = false
                if (! pol.hasReportedPayroll && this.validate(pol)) {
                    this.createPolicyReport(pol, payrollReports);
                }
            })

            if (payrollReports.length === this.countPolicyReportsNeeded(this.policies)) {
                this.submitReports(payrollReports, e);

            } else if (this.error == null) {
                this.error = this.createErrorWithMessage("Whoops")
            }

            // here is a simple fix to make the state maintain itself on page refresh
            // we were shoving state into the policies object (entered payrolls and etc) so keep this on the sessionStorage like everything else...
            store("policies", this.policies)

            if (this.error != null) {
                window.scrollTo(0, 0)
            }
        },
        createPolicyReport: function (pol, payrollReports) {
            let payrollLines = []
            let total = 0
            let premiumTotal = 0
            pol.jurisdictions.forEach(jurisdiction => {
                jurisdiction.locations.forEach(location => {
                    premiumTotal += this.taxTotal(location) + this.premiumTotal(location)
                    location.classCodes.forEach(clazz => {
                        clazz.payroll = this.stripNonFloatChars(clazz.payroll)
                        let quantity = clazz.payroll
                        if (!quantity) {
                            quantity = "0"
                        } else if (quantity.charAt(0) === "." && quantity.length > 1) {
                            quantity = "0" + quantity
                        } else if (quantity.endsWith(".") && quantity.length > 1) {
                            quantity = quantity.slice(0, -1)
                        }
                        total += parseFloat(quantity)
                        payrollLines.push({
                            classCodeId: clazz.id,
                            payroll: quantity,
                        })
                    })
                })
            })
            payrollReports.push({
                policyId: pol.id,
                policyNumber: pol.name,
                reportPeriodStart: this.reporting.payrollPeriodStart,
                payrollLines,
                total,
                premiumTotal: premiumTotal
            })
            pol.hasPayrollReport = true
        },
        submitReports: function (payrollReports, e) {
            this.$store.dispatch("submitPayroll", payrollReports).then(() => {
                this.policies.forEach(policy => {
                    policy.hasReportedPayroll = true
                })
                this.$router.push({name: "Confirmation"})

            }).catch(error => {
                this.error = error
                e.target.focus()
            })
        }
    },
    beforeRouteUpdate() {
        this.onClear()
    },
    props: ["gatherPayroll"]
}
</script>